import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { Coins, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PackageCard = ({ p, selected, onSelect }) => (
  <button onClick={() => onSelect(p.id)} data-testid={`credit-package-${p.id}`}
    className={`text-left p-8 border transition-colors duration-300 relative ${selected ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10 bg-[#121418] hover:border-white/30"}`}>
    {selected && <Check className="absolute top-4 right-4 w-4 h-4 text-[#D4AF37]" />}
    <p className="font-serif text-6xl">{p.credits}</p>
    <p className="eyebrow mt-2">crediti</p>
    <p className="font-mono text-xl mt-8">€ {p.price.replace(".", ",")}</p>
    <p className="text-xs text-neutral-500 mt-1">€ {(Number(p.price) / p.credits).toFixed(2).replace(".", ",")} / credito</p>
  </button>
);

const Checkout = ({ clientId, currency, packageId, onPaid }) => (
  <PayPalScriptProvider options={{ clientId, currency, intent: "capture" }}>
    <PayPalButtons
      style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay" }}
      forceReRender={[packageId]}
      createOrder={async () => (await api.post("/payments/paypal/create-order", { package_id: packageId })).data.id}
      onApprove={async (data) => {
        try {
          const r = await api.post("/payments/paypal/capture", { order_id: data.orderID });
          onPaid(r.data.credits);
        } catch (e) { toast.error(formatApiError(e)); }
      }}
      onError={() => toast.error("Pagamento non riuscito. Riprova.")}
    />
  </PayPalScriptProvider>
);

export default function CreditsPage() {
  const { user, refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [config, setConfig] = useState(null);
  const [selected, setSelected] = useState("pack_20");

  const load = () => api.get("/credits").then((r) => setInfo(r.data));
  useEffect(() => { load(); api.get("/payments/config").then((r) => setConfig(r.data)); }, []);

  const onPaid = (credits) => {
    toast.success(`Pagamento completato. Saldo: ${credits} crediti`);
    load();
    refresh();
  };

  if (!info) return <p className="eyebrow text-center py-40">Caricamento…</p>;

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-10 pt-16" data-testid="credits-page">
      <div className="grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <p className="eyebrow">Il tuo wallet</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3">Crediti</h1>
          <p className="text-neutral-400 mt-4 max-w-xl">
            {user.role === "hostess"
              ? <>Metti il tuo profilo <span className="text-white">in evidenza</span> in cima alle ricerche: 3 giorni = <span className="text-white">10 crediti</span>, 7 giorni = <span className="text-white">20 crediti</span>.</>
              : <>Pubblicare un evento costa <span className="text-white">{info.event_cost} crediti</span>. Sbloccare i contatti di una hostess (Telegram, email e telefono) costa <span className="text-white">{info.unlock_cost} crediti</span>.</>}
          </p>
        </div>
        <div className="lg:col-span-4 border border-[#D4AF37]/30 p-8 shadow-[0_0_25px_rgba(212,175,55,0.08)]">
          <p className="eyebrow !text-neutral-500">Saldo attuale</p>
          <p className="font-serif text-6xl mt-2 flex items-center gap-3" data-testid="credits-balance"><Coins className="w-8 h-8 text-[#D4AF37]" />{info.credits}</p>
        </div>
      </div>

      <section className="mt-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 grid sm:grid-cols-3 gap-4">
          {info.packages.map((p) => <PackageCard key={p.id} p={p} selected={selected === p.id} onSelect={setSelected} />)}
        </div>
        <div className="lg:col-span-4" data-testid="paypal-checkout">
          <p className="eyebrow mb-4">Paga con PayPal</p>
          {config?.client_id ? <Checkout clientId={config.client_id} currency={config.currency} packageId={selected} onPaid={onPaid} />
            : <p className="text-sm text-neutral-500 border border-white/10 p-6" data-testid="paypal-not-configured">I pagamenti non sono ancora attivi.</p>}
        </div>
      </section>

      <section className="mt-20">
        <h2 className="font-serif text-3xl">Movimenti</h2>
        <div className="mt-6 divide-y divide-white/5 border-y border-white/5" data-testid="credits-transactions">
          {info.transactions.length === 0 && <p className="py-10 text-center text-neutral-500">Nessun movimento</p>}
          {info.transactions.map((t) => (
            <div key={t.id} className="flex items-center justify-between py-4 px-2 text-sm" data-testid={`credit-tx-${t.id}`}>
              <div>
                <p className="text-neutral-200">{t.reason}</p>
                <p className="text-[11px] font-mono text-neutral-500 mt-1">{new Date(t.created_at).toLocaleString("it-IT")}</p>
              </div>
              <span className={`font-mono ${t.amount > 0 ? "text-emerald-400" : "text-neutral-400"}`}>{t.amount > 0 ? "+" : ""}{t.amount}</span>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
